import { useTranslation } from 'react-i18next';
import { ChevronRight, FileText } from 'lucide-react';
import { useStoreQuery } from '../db/context';
import { useUi } from '../state/ui';
import { Snippet } from './Snippet';

export function BacklinksPanel({ pageId, names }: { pageId: string; names: string[] }) {
  const { t } = useTranslation();
  const open = useUi((s) => s.open);
  const key = names.join('|');
  const data = useStoreQuery(
    async (s) => ({
      links: await s.backlinks(pageId),
      mentions: await s.unlinkedMentions(pageId, names),
    }),
    [pageId, key],
    ['index', 'pages'],
  );
  if (!data) return null;
  // One entry per source page; keep the first context.
  const links = [...new Map(data.links.map((l) => [l.pageId, l])).values()];

  const item = (id: string, title: string, body: React.ReactNode) => (
    <li key={id}>
      <button
        type="button"
        onClick={(e) => open({ kind: 'page', pageId: id }, e.ctrlKey || e.metaKey)}
        className="w-full rounded-md px-2 py-1.5 text-left hover:bg-bg-hover"
      >
        <span className="flex items-center gap-1.5 font-medium">
          <FileText className="size-3.5 text-fg-muted" />
          {title || t('page.untitled')}
        </span>
        {body && <span className="mt-0.5 line-clamp-2 block text-xs text-fg-muted">{body}</span>}
      </button>
    </li>
  );

  return (
    <section
      className="mt-12 border-t border-border pt-4 text-sm"
      aria-label={t('backlinks.title')}
    >
      <details open={links.length > 0}>
        <summary className="flex cursor-pointer list-none items-center gap-1 font-medium text-fg-muted">
          <ChevronRight className="size-4 transition-transform [details[open]_&]:rotate-90" />
          {t('backlinks.linked', { count: links.length })}
        </summary>
        {links.length === 0 ? (
          <p className="px-2 py-1 text-fg-muted">{t('backlinks.none')}</p>
        ) : (
          <ul className="mt-1">{links.map((l) => item(l.pageId, l.title, l.context))}</ul>
        )}
      </details>
      <details className="mt-3">
        <summary className="flex cursor-pointer list-none items-center gap-1 font-medium text-fg-muted">
          <ChevronRight className="size-4 transition-transform [details[open]_&]:rotate-90" />
          {t('backlinks.unlinked', { count: data.mentions.length })}
        </summary>
        {data.mentions.length === 0 ? (
          <p className="px-2 py-1 text-fg-muted">{t('backlinks.none')}</p>
        ) : (
          <ul className="mt-1">
            {data.mentions.map((m) => item(m.pageId, m.title, <Snippet text={m.snippet} />))}
          </ul>
        )}
      </details>
    </section>
  );
}
